import {action, makeObservable, observable} from 'mobx';

import {DownloadResponse, fileUploader} from '../../api/fileUploader';
import {BRAND_NAME} from '../../config';
import {uint8ArrayToUuid} from '../../utils/arrayUtils';
import * as cache from '../../utils/cache';
import {FileData} from '../../utils/file/fileReaders';
import {FileTypeParser} from '../../utils/file/fileTypeBrowser';
import {findMimeType} from '../../utils/file/mimeTypes';
import {WorkspaceStore} from '../Workspaces';
import AttachmentProgress, {IAttachmentProgress} from './AttachmentProgress';

const CACHE_MAX_BYTES = 10 * 1024 * 1024; // 10 Mb
export const DOWNLOAD_CANCELED_ERROR_MESSAGE = 'Download canceled';

type PreparedAttachment = {
  src: string;
  mimeType?: string;
};

interface OnAttachmentProgress {
  (
    progress: number, // Float between 0 and 1.
    progressPercent: number,
    ...args: any[]
  ): void;

  isCanceled?: boolean;
  acceptsBuffer?: boolean;
}

interface IAttachmentStore {
  fetch(attachmentID?: Uint8Array | null, channelID?: Uint8Array | null): Promise<PreparedAttachment>;
}

export default class AttachmentStore implements IAttachmentStore {
  constructor(protected workspace: WorkspaceStore) {
    makeObservable(this);
  }

  @observable public progressMap: {[attachmentID: string]: AttachmentProgress | undefined} = {};

  private attachmentIDToString_ = (attachmentID?: Uint8Array | null): string => {
    if (!attachmentID) {
      return '';
    }
    return uint8ArrayToUuid(attachmentID);
  };

  public hasInProgress = (attachmentID?: Uint8Array | null): boolean => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);
    if (!attachmentID) {
      return false;
    }

    return !!this.progressMap[attachmentUuid];
  };

  @action private addToProgress = (attachmentID: Uint8Array, data: IAttachmentProgress) => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);
    const fileProgress = this.progressMap[attachmentUuid];

    //console.debug(`%c----->addToProgress: ${uint8ArrayToUuid(attachmentID)}`, 'color: red');
    if (fileProgress) {
      fileProgress.fill(data);
    } else {
      this.progressMap[attachmentUuid] = new AttachmentProgress(data);
    }
  };

  @action private updateFileProgress = (attachmentID: Uint8Array, data: Partial<IAttachmentProgress>) => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);
    const fileProgress = this.progressMap[attachmentUuid];

    if (
      fileProgress?.progress === data.progress &&
      fileProgress?.inProgress === data.inProgress &&
      fileProgress?.error === data.error
    ) {
      return;
    }

    console.debug(`%cDownload progress: ${attachmentUuid} ${data.progressPercent}`, 'color: gray');
    fileProgress?.fill({
      progress: 0,
      progressPercent: 0,
      ...data,
    });
  };

  @action private setError = (attachmentID?: Uint8Array | null, error?: string) => {
    if (!attachmentID) return;

    console.debug(`%c----->setError: ${uint8ArrayToUuid(attachmentID)}`, 'color: red', error);
    this.updateFileProgress(attachmentID, {
      inProgress: false,
      progress: 0,
      progressPercent: 0,
      error: error || 'error',
    });
  };

  @action private removeFromProgress = (attachmentID?: Uint8Array | null) => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);

    if (this.progressMap[attachmentUuid]) {
      this.progressMap[attachmentUuid]?.fill({
        isDone: true,
        progress: 1,
        progressPercent: 100,
      });
      delete this.progressMap[attachmentUuid];
    }
  };

  @action cancelDownload = (attachmentID?: Uint8Array | null) => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);
    this.progressMap[attachmentUuid]?.fill({isCanceled: true});
  };

  private isCanceled_ = (attachmentID?: Uint8Array | null): boolean => {
    const attachmentUuid = this.attachmentIDToString_(attachmentID);

    return !!this.progressMap[attachmentUuid]?.isCanceled;
  };

  public fetch = async (
    attachmentID?: Uint8Array | null,
    channelID?: Uint8Array | null,
    fileName?: string | null,
    onProgress?: OnAttachmentProgress,
    callbackUniqueId?: string,
    scope?: string | null,
  ): Promise<PreparedAttachment> => {
    if (!attachmentID || !channelID) {
      return {
        src: '',
      };
    }

    const attachmentKey = this.formatAttachmentKey_(attachmentID, channelID);

    if (this.memoryCache_.has(attachmentKey)) {
      // console.debug(`%c----->fetch FromCache ID: ${uint8ArrayToUuid(attachmentID)}`, 'color: green; font-weight: bold', this.memoryCache_.get(attachmentKey));
      return this.memoryCache_.get(attachmentKey) as PreparedAttachment;
    }

    this.addToProgress(attachmentID, {
      isUpload: false,
      isDone: false,
      inProgress: true,
      progress: 0,
      progressPercent: 0,
      channelID,
      fileName,
      scope,
      error: null,
      isCanceled: false,
    });

    let promise = this.fetchPromises_.get(attachmentKey);

    if (!promise) {
      promise = this.fetchFromCacheOrRemote_(attachmentID, channelID, fileName, scope)
        .then((result) => {
          this.removeFromProgress(attachmentID);
          return result;
        })
        .catch((err) => {
          console.debug('fetch Error: ', err);
          this.setError(attachmentID, err?.message);
          throw err;
        })
        .finally(() => {
          this.fetchPromises_.delete(attachmentKey);
          this.progressCallbacks_.delete(attachmentKey);
        });

      this.fetchPromises_.set(attachmentKey, promise);
    }

    if (onProgress && callbackUniqueId) {
      let activeCallbacks = this.progressCallbacks_.get(attachmentKey);
      if (!activeCallbacks) {
        activeCallbacks = new Map<string, OnAttachmentProgress>();
        this.progressCallbacks_.set(attachmentKey, activeCallbacks);
      }
      activeCallbacks.set(callbackUniqueId, onProgress);
    }

    return promise;
  };

  public get = async (
    attachmentID: Uint8Array,
    channelID?: Uint8Array | null,
    fileName?: string | null,
    scope?: string | null,
  ): Promise<{error?: {message: string}; res?: PreparedAttachment}> => {
    try {
      const res = await this.fetch(attachmentID, channelID, fileName, undefined, undefined, scope);
      return {res};
    } catch (err: any) {
      return {error: {message: err?.message}};
    }
  };

  public getFromCache = (attachmentID?: Uint8Array | null, channelID?: Uint8Array | null): PreparedAttachment => {
    if (!attachmentID || !channelID) {
      return {
        src: '',
      };
    }

    const attachmentKey = this.formatAttachmentKey_(attachmentID, channelID);

    if (this.memoryCache_.has(attachmentKey)) {
      // console.debug(`%c----->fetch FromCache ID: ${uint8ArrayToUuid(attachmentID)}`, 'color: green; font-weight: bold', this.memoryCache_.get(attachmentKey));
      return this.memoryCache_.get(attachmentKey) as PreparedAttachment;
    }

    return {
      src: '',
    };
  };

  private memoryCache_ = new Map<string, PreparedAttachment>();
  private fetchPromises_ = new Map<string, Promise<PreparedAttachment>>();
  private progressCallbacks_ = new Map<string, Map<string, OnAttachmentProgress>>();

  private formatAttachmentKey_ = (attachmentID: Uint8Array, channelID: Uint8Array): string => {
    return `${uint8ArrayToUuid(channelID)}_${uint8ArrayToUuid(attachmentID)}`;
  };

  private formatStoreKey_ = (scope?: string | null): string => {
    if (scope) {
      return `${BRAND_NAME}_${scope}`;
    }
    return `${BRAND_NAME}_${uint8ArrayToUuid(this.workspace.id)}`;
  };

  private prepareAttachment_ = (
    attachmentData: Blob,
    attachmentID: Uint8Array,
    fileName?: string | null,
  ): PreparedAttachment => {
    const objectUrl = URL.createObjectURL(attachmentData);
    console.debug(
      `%c----->link attachment ${uint8ArrayToUuid(attachmentID)} (${fileName}) with Url: ${objectUrl}`,
      'color: gray',
    );
    return {
      src: objectUrl,
      mimeType: attachmentData.type,
    };
  };

  public putToCache = (channelID: Uint8Array, dataFile: FileData, attachmentID: Uint8Array): void => {
    const attachmentKey = this.formatAttachmentKey_(attachmentID, channelID);

    this.memoryCache_.set(attachmentKey, {
      src: dataFile.objectUrl,
      mimeType: dataFile.mimeType,
    });
  };

  protected fetchFromCacheOrRemote_ = async (
    attachmentID: Uint8Array,
    channelID: Uint8Array,
    fileName?: string | null,
    scope?: string | null,
  ): Promise<PreparedAttachment> => {
    const attachmentKey = this.formatAttachmentKey_(attachmentID, channelID);
    const storeKey = this.formatStoreKey_(scope);

    const cached = await cache.fetch(storeKey, attachmentKey, cache.CacheType.Blob, false);

    if (cached) {
      const prepared = this.prepareAttachment_(cached, attachmentID, fileName);
      this.memoryCache_.set(attachmentKey, prepared);
      return prepared;
    }

    const {error, res} = await this.getAttachment(attachmentID, fileName);

    if (error || !res?.content) {
      throw new Error(error?.message || '');
    }

    let mimeType = findMimeType(fileName);
    const sourceBuffer = res.content.buffer as ArrayBuffer;
    const arrayBuffer = sourceBuffer.slice(res.content.byteOffset, res.content.byteOffset + res.content.byteLength);
    let blob = new Blob([arrayBuffer], {type: mimeType});

    if (!mimeType) {
      const info = new FileTypeParser(blob).parse(res.content);
      if (info?.mime) {
        mimeType = info?.mime;
        blob = new Blob([arrayBuffer], {type: info?.mime});
      }
    }

    // console.debug(`%c----->fetchFromCacheOrRemote_ ID: ${uint8ArrayToUuid(attachmentID)}`, 'color: green', fileName, mimeType, res.content.length);

    const canCache = blob.size !== 0 && blob.size <= CACHE_MAX_BYTES;
    if (canCache) {
      cache.put(storeKey, attachmentKey, blob);
    }

    const prepared = this.prepareAttachment_(blob, attachmentID, fileName);
    // console.debug(`%c----->fetchFromCacheOrRemote_ ID: ${uint8ArrayToUuid(attachmentID)}`, 'color: green; font-weight: bold', prepared);

    this.memoryCache_.set(attachmentKey, prepared);

    return prepared;
  };

  public getAttachment = async (attachmentID: Uint8Array, fileName?: string | null): Promise<DownloadResponse> => {
    const response = await fileUploader.download({
      attachmentID,
      fileName,
      onDownloadProgress: (data) => {
        //console.debug(`%c----->getAttachment progress ID: ${uint8ArrayToUuid(attachmentID)}`, 'color: gray', data);
        const {error, progress} = data;
        const progressPercent = Math.floor((progress || 0) * 100);
        this.updateFileProgress(attachmentID, {
          error,
          progress: progress || 0,
          progressPercent,
        });
      },
    });

    if (this.isCanceled_(attachmentID)) {
      console.debug(`%c----->getAttachment isCanceled: ${uint8ArrayToUuid(attachmentID)}`, 'color: red');

      return {error: {message: DOWNLOAD_CANCELED_ERROR_MESSAGE}};
    }

    return response;
  };
}
